import { Select } from '@tempo-io/sui-select';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ItemPicklistValue } from '@/common/api/types/items';
import { usePicklistOptionsQuery } from '@/common/queries/useGetCustomFieldsQuery';
import { associateById } from '@/common/utils/associateById';
import { EmptyFieldValue } from '@/features/items/components/item-field-value/EmptyFieldValue';
import { Field } from '@/features/items/components/item-field-value/Field';
import { FieldWithStatusIndicator } from '@/features/items/components/item-field-value/FieldWithStatusIndicator';
import type { PicklistOption } from './types';

export const PicklistField = ({
  id,
  name,
  value,
  isEditable = false,
  onChange,
  isPending,
  isSuccess,
  isError = false,
  error,
  excludedOptionIds = [],
  autoFocus = false,
  onMenuClose,
  placeholder,
  isClearable = true,
  options: optionsProp,
}: {
  id: string;
  name: string;
  value?: ItemPicklistValue;
  isEditable?: boolean;
  onChange: (value: number | null) => void;
  isPending?: boolean;
  isSuccess?: boolean;
  isError?: boolean;
  error?: Error | null;
  excludedOptionIds?: number[];
  autoFocus?: boolean;
  onMenuClose?: () => void;
  placeholder?: string;
  isClearable?: boolean;
  options?: PicklistOption[] | null;
}) => {
  const { data: fetchedOptions } = usePicklistOptionsQuery({
    customFieldId: Number(id),
    enabled: !optionsProp,
  });
  const data = optionsProp ?? fetchedOptions;
  const { t } = useTranslation();

  const picklistOptionsById = associateById(data);

  const inputId = `picklist-field-${id}`;
  const picklistOptionName = value ? picklistOptionsById[value.PICKLIST]?.name : undefined;

  const allOptions: PicklistOption[] = data || [];
  const options = allOptions.filter((option) => !excludedOptionIds.includes(option.id));

  const [selectedOption, setSelectedOption] = useState<PicklistOption | null>(null);

  useEffect(() => {
    const option =
      value && data ? (data.find((option) => option.id === value.PICKLIST) ?? null) : null;
    setSelectedOption(option);
  }, [value, data]);

  if (isEditable) {
    return (
      <FieldWithStatusIndicator
        inputId={inputId}
        name={name}
        isPending={isPending}
        isSuccess={isSuccess}
      >
        <div className="flex flex-col gap-1">
          <Select<PicklistOption>
            height={40}
            options={options}
            value={selectedOption}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.id.toString()}
            onChange={(option) => {
              option ? onChange(option.id) : onChange(null);
              setSelectedOption(option);
            }}
            isClearable={isClearable}
            isSearchable={false}
            isDisabled={isPending}
            placeholder={placeholder || t(($) => $.common.settings.customFields.select)}
            autoFocus={autoFocus}
            openMenuOnFocus={autoFocus}
            onMenuClose={onMenuClose}
          />
          {isError && error && <span className="text-xs text-text-error">{error.message}</span>}
        </div>
      </FieldWithStatusIndicator>
    );
  }

  return (
    <Field inputId={inputId} name={name}>
      <div className="text-sm text-text-neutral-primary">
        {picklistOptionName || <EmptyFieldValue />}
      </div>
    </Field>
  );
};
